import type { ReactNode } from "react"
import { Fragment } from "react"

import { cn } from "@productlane/lib/src/browser"

export interface ReportingTooltipEntry {
	name?: string | number
	value?: string | number
	color?: string
	dataKey?: string | number
	payload?: { rangeLabel?: string; [key: string]: unknown }
}

export interface ReportingTooltipTable {
	// Headers for the series, the value, and the metric column.
	headers: [string, string, string]
	metric: (entry: ReportingTooltipEntry) => ReactNode
}

interface ReportingChartTooltipProps {
	active?: boolean
	payload?: ReportingTooltipEntry[]
	label?: string | number
	formatter?: (value: number, name: string) => [ReactNode, ReactNode]
	labelFormatter?: (label: string) => ReactNode
	hideZero?: boolean
	// Lays the rows out as a table with a header, so a second number per row
	// says what it measures.
	table?: ReportingTooltipTable
	// For a narrow chart: no header row and no zero rows, so it stays small.
	compact?: boolean
}

export const ReportingChartTooltip = ({
	active,
	payload,
	label,
	formatter,
	labelFormatter,
	hideZero = false,
	table,
	compact = false,
}: ReportingChartTooltipProps) => {
	if (!active || !payload?.length) return null

	const entries = payload.filter((entry) => {
		if (entry.value == null || Number.isNaN(Number(entry.value))) return false
		if ((hideZero || compact) && Number(entry.value) === 0) return false
		return true
	})
	if (entries.length === 0) return null

	// A bucketed series carries the exact window it covers; the axis tick only
	// has room for where it starts.
	const rangeLabel = payload.find((entry) => entry.payload?.rangeLabel)?.payload
		?.rangeLabel
	const labelText = rangeLabel ?? (label == null ? null : String(label))

	const cells = entries.map((entry, index) => {
		const name = String(entry.name ?? entry.dataKey ?? "")
		const rawValue = Number(entry.value)
		const [displayValue, displayName] = formatter
			? formatter(rawValue, name)
			: [String(entry.value), name]
		return { key: `${name}-${index}`, entry, displayValue, displayName }
	})

	if (compact && table) {
		const total = entries.reduce((sum, entry) => sum + Number(entry.value), 0)
		return (
			<div className="min-w-[160px] rounded-md bg-white px-2.5 py-1.5 text-[11px] shadow-md ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
				<div className="mb-1 flex items-baseline justify-between gap-3">
					<span className="font-medium text-primary">
						{labelText && labelFormatter
							? labelFormatter(labelText)
							: labelText}
					</span>
					<span className="tabular-nums text-tertiary">
						{total} {table.headers[1].toLowerCase()}
					</span>
				</div>
				<ul className="space-y-0.5">
					{cells.map(({ key, entry, displayValue, displayName }) => {
						const metric = table.metric(entry)
						return (
							<li key={key} className="flex items-center gap-1.5">
								<span
									className="h-1.5 w-1.5 flex-none rounded-full"
									style={{ backgroundColor: entry.color }}
								/>
								<span className="truncate text-secondary">{displayName}</span>
								<span className="ml-auto pl-3 tabular-nums text-primary">
									{displayValue}
									{metric == null ? null : (
										<span className="text-tertiary"> · {metric}</span>
									)}
								</span>
							</li>
						)
					})}
				</ul>
			</div>
		)
	}

	return (
		<div className="rounded-lg bg-white px-3 py-2 text-xs ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			{labelText ? (
				<p className="mb-1.5 font-medium text-primary">
					{labelFormatter ? labelFormatter(labelText) : labelText}
				</p>
			) : null}
			{table ? (
				<div className="max-h-64 overflow-y-auto">
					<div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 gap-y-1">
						{table.headers.map((header, index) => (
							<span
								key={header}
								className={cn(
									"sticky top-0 bg-white pb-1 font-medium text-tertiary dark:bg-gray-900",
									index > 0 && "text-right",
								)}
							>
								{header}
							</span>
						))}
						{cells.map(({ key, entry, displayValue, displayName }) => (
							<Fragment key={key}>
								<span className="flex items-center gap-2 text-tertiary">
									<span
										className="h-2 w-2 shrink-0 rounded-full"
										style={{ backgroundColor: entry.color }}
									/>
									{displayName}
								</span>
								<span className="text-right font-medium tabular-nums text-primary">
									{displayValue}
								</span>
								<span className="text-right tabular-nums text-secondary">
									{table.metric(entry)}
								</span>
							</Fragment>
						))}
					</div>
				</div>
			) : (
				<ul className="max-h-64 space-y-1 overflow-y-auto">
					{cells.map(({ key, entry, displayValue, displayName }) => (
						<li key={key} className="flex items-center gap-2 text-secondary">
							<span
								className="h-2 w-2 shrink-0 rounded-full"
								style={{ backgroundColor: entry.color }}
							/>
							<span className="text-tertiary">{displayName}</span>
							<span className="font-medium text-primary">{displayValue}</span>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}
